import React, { useRef, useState, useEffect } from "react";
import {
  Stack,
  Paper,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Text,
  Checkbox,
  SimpleGrid,
  ActionIcon,
  ThemeIcon,
  Grid,
  Timeline,
  Accordion,
  Badge,
  UnstyledButton,
  Box,
  Menu,
  Tabs,
} from "@mantine/core";
import { IconPlus, IconTrash, IconDotsVertical } from "@tabler/icons";
import RemoteSelect2 from "../remoteSelect2";
import {
  MCA_APPROVAL_TYPES,
  defaultStepNotification,
  formatStaffLabel,
  isFileFieldType,
} from "../../data/mcaEntities";
import McaStepFieldsEditor from "./McaStepFieldsEditor";
import McaSetValuesEditor from "./McaSetValuesEditor";
import McaStepNotificationsList from "./McaStepNotificationsList";

const approvalTypeLabel = (value) =>
  MCA_APPROVAL_TYPES.find((t) => t.value === value)?.label ?? value;

const emptyStep = (order) => ({
  order,
  name: "",
  description: "",
  approvalType: "ANY",
  approvers: [],
  approverRoles: [],
  checklist: [],
  setValues: [],
  notifications: [],
  stepFields: [],
  timeoutHours: null,
});

const stepKey = (idx) => `step-${idx}`;

const stepActionsSummary = (step) => {
  const parts = [];
  const checklist = step.checklist?.length ?? 0;
  const fields = step.stepFields?.length ?? 0;
  const notifications = step.notifications?.length ?? 0;
  const setValues = step.setValues?.length ?? 0;
  if (checklist) parts.push(`${checklist} checklist`);
  if (fields) parts.push(`${fields} input field${fields > 1 ? "s" : ""}`);
  if (notifications) parts.push(`${notifications} notification${notifications > 1 ? "s" : ""}`);
  if (setValues) parts.push(`${setValues} set value${setValues > 1 ? "s" : ""}`);
  return parts.length ? parts.join(" · ") : "Optional — expand to configure";
};

const StepFields = ({
  step,
  idx,
  updateStep,
  updateChecklist,
  updateSetValues,
  updateNotifications,
  updateStepFields,
  statusFields,
}) => (
  <>
    <SimpleGrid
      cols={2}
      spacing="md"
      breakpoints={[{ maxWidth: "md", cols: 1 }]}
    >
      <Stack spacing="sm">
        <TextInput
          label="Step name"
          required
          size="xs"
          value={step.name}
          onChange={(e) => updateStep(idx, { name: e.target.value })}
        />
        <Textarea
          label="Description"
          size="xs"
          minRows={4}
          autosize
          maxRows={4}
          value={step.description ?? ""}
          onChange={(e) => updateStep(idx, { description: e.target.value })}
        />
      </Stack>

      <Stack spacing="sm">
        <Select
          label="Approval type"
          size="xs"
          data={MCA_APPROVAL_TYPES}
          value={step.approvalType}
          onChange={(v) => updateStep(idx, { approvalType: v })}
        />
        <RemoteSelect2
          form={{
            values: step,
            setFieldValue: (field, val) => updateStep(idx, { [field]: val }),
            markUserEdited: () => {},
          }}
          name="approverRoles"
          label="Approvers by roles"
          apiEntity="userRole"
          labelField="name"
          multiple
          size="xs"
          placeholder="Search roles"
          searchFields={["name"]}
        />
        <RemoteSelect2
          form={{
            values: step,
            setFieldValue: (field, val) => updateStep(idx, { [field]: val }),
            markUserEdited: () => {},
          }}
          name="approvers"
          label="Approvers by user"
          apiEntity="user"
          labelRender={formatStaffLabel}
          multiple
          size="xs"
          placeholder="Search users"
          searchFields={["name", "staffId", "cname", "email", "aliasName"]}
        />
      </Stack>
    </SimpleGrid>

    <Accordion variant="separated" defaultValue={[]} mt="xl">
      <Accordion.Item value="actions">
        <Accordion.Control>
          <Group spacing="xs" noWrap>
            <Text size="sm" weight={600}>
              Step actions
            </Text>
            <Text size="xs" color="dimmed">
              {stepActionsSummary(step)}
            </Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Tabs defaultValue="checklist" size="xs">
            <Tabs.List>
              <Tabs.Tab value="checklist">
                Checklist items
                {(step.checklist?.length ?? 0) > 0
                  ? ` (${step.checklist.length})`
                  : ""}
              </Tabs.Tab>
              <Tabs.Tab value="stepFields">
                Input Fields
                {(step.stepFields?.length ?? 0) > 0
                  ? ` (${step.stepFields.length})`
                  : ""}
              </Tabs.Tab>
              <Tabs.Tab value="notification">
                Notification
                {(step.notifications?.length ?? 0) > 0
                  ? ` (${step.notifications.length})`
                  : ""}
              </Tabs.Tab>
              <Tabs.Tab value="setValue">
                Set Value
                {(step.setValues?.length ?? 0) > 0
                  ? ` (${step.setValues.length})`
                  : ""}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="checklist" pt="xs" sx={{ width: "100%" }}>
              <Stack spacing="xs" sx={{ width: "100%" }}>
                {(step.checklist ?? []).map((item, cIdx) => (
                  <Group
                    key={cIdx}
                    spacing="sm"
                    align="flex-end"
                    noWrap
                    sx={{ width: "100%" }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <TextInput
                        placeholder="Checklist title"
                        size="xs"
                        sx={{ width: "100%" }}
                        value={item.title}
                        onChange={(e) => {
                          const checklist = [...(step.checklist ?? [])];
                          checklist[cIdx] = { ...item, title: e.target.value };
                          updateChecklist(idx, checklist);
                        }}
                      />
                    </Box>
                    <Checkbox
                      size="xs"
                      label="Required"
                      sx={{ flexShrink: 0 }}
                      checked={!!item.required}
                      onChange={(e) => {
                        const checklist = [...(step.checklist ?? [])];
                        checklist[cIdx] = {
                          ...item,
                          required: e.currentTarget.checked,
                        };
                        updateChecklist(idx, checklist);
                      }}
                    />
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      sx={{ flexShrink: 0 }}
                      onClick={() =>
                        updateChecklist(
                          idx,
                          (step.checklist ?? []).filter((_, i) => i !== cIdx)
                        )
                      }
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                ))}
                <Button
                  size="xs"
                  w="fit-content"
                  variant="light"
                  leftIcon={<IconPlus size={14} />}
                  onClick={() =>
                    updateChecklist(idx, [
                      ...(step.checklist ?? []),
                      { title: "", required: false },
                    ])
                  }
                >
                  Add checklist item
                </Button>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="stepFields" pt="xs">
              <McaStepFieldsEditor
                stepFields={step.stepFields ?? []}
                onChange={(stepFields) => updateStepFields(idx, stepFields)}
              />
            </Tabs.Panel>

            <Tabs.Panel value="notification" pt="xs">
              <McaStepNotificationsList
                notifications={step.notifications ?? []}
                onChange={(notifications) => updateNotifications(idx, notifications)}
              />
            </Tabs.Panel>

            <Tabs.Panel value="setValue" pt="xs">
              <McaSetValuesEditor
                setValues={step.setValues ?? []}
                onChange={(setValues) => updateSetValues(idx, setValues)}
                statusFields={statusFields}
              />
            </Tabs.Panel>
          </Tabs>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  </>
);

const McaStepBuilder = ({ steps, onChange, statusFields = [] }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [openAccordions, setOpenAccordions] = useState([stepKey(0)]);
  const panelRefs = useRef({});

  useEffect(() => {
    if (activeStep >= steps.length) {
      setActiveStep(Math.max(0, steps.length - 1));
    }
  }, [steps.length, activeStep]);

  const updateStep = (idx, patch) => {
    onChange(steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const updateChecklist = (stepIdx, checklist) => {
    updateStep(stepIdx, { checklist });
  };

  const updateSetValues = (stepIdx, setValues) => {
    updateStep(stepIdx, { setValues });
  };

  const updateNotifications = (stepIdx, notifications) => {
    updateStep(stepIdx, { notifications });
  };

  const updateStepFieldsConfig = (stepIdx, stepFields) => {
    updateStep(stepIdx, { stepFields });
  };

  const removeStep = (idx) => {
    const next = steps
      .filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, order: i + 1 }));
    onChange(next);
    const nextActive = Math.min(activeStep, next.length - 1);
    setActiveStep(nextActive);
    setOpenAccordions((prev) =>
      prev
        .filter((k) => k !== stepKey(idx))
        .map((k) => {
          const n = Number(k.replace("step-", ""));
          return n > idx ? stepKey(n - 1) : k;
        })
    );
  };

  const duplicateStep = (idx) => {
    const source = steps[idx];
    const copy = {
      ...source,
      name: source.name?.trim()
        ? `${source.name.trim()} (copy)`
        : `Step ${idx + 2}`,
      approvers: [...(source.approvers ?? [])],
      approverRoles: [...(source.approverRoles ?? [])],
      checklist: (source.checklist ?? []).map((item) => ({ ...item })),
      setValues: (source.setValues ?? []).map((item) => ({ ...item })),
      notifications: (source.notifications ?? []).map((n) => ({
        ...defaultStepNotification(),
        ...n,
        notifyUsers: [...(n.notifyUsers ?? [])],
        notifyRoles: [...(n.notifyRoles ?? [])],
        notifyEmails: [...(n.notifyEmails ?? [])],
      })),
      stepFields: (source.stepFields ?? []).map((item) => ({ ...item })),
    };
    const next = [
      ...steps.slice(0, idx + 1),
      copy,
      ...steps.slice(idx + 1),
    ].map((s, i) => ({ ...s, order: i + 1 }));
    onChange(next);

    const newIdx = idx + 1;
    setActiveStep(newIdx);
    setOpenAccordions((prev) => {
      const remapped = prev.map((k) => {
        const n = Number(k.replace("step-", ""));
        return n > idx ? stepKey(n + 1) : k;
      });
      return remapped.includes(stepKey(newIdx))
        ? remapped
        : [...remapped, stepKey(newIdx)];
    });
    setTimeout(() => {
      panelRefs.current[stepKey(newIdx)]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 200);
  };

  const addStep = () => {
    const nextIdx = steps.length;
    onChange([...steps, emptyStep(nextIdx + 1)]);
    setActiveStep(nextIdx);
    setOpenAccordions((prev) => [...prev, stepKey(nextIdx)]);
    setTimeout(() => {
      panelRefs.current[stepKey(nextIdx)]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 200);
  };

  const focusStep = (idx) => {
    setActiveStep(idx);
    setOpenAccordions((prev) =>
      prev.includes(stepKey(idx)) ? prev : [...prev, stepKey(idx)]
    );
    setTimeout(() => {
      panelRefs.current[stepKey(idx)]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 200);
  };

  const approverSummary = (step) => {
    const roles = step.approverRoles?.length ?? 0;
    const users = step.approvers?.length ?? 0;
    const parts = [];
    if (roles) parts.push(`${roles} role${roles > 1 ? "s" : ""}`);
    if (users) parts.push(`${users} user${users > 1 ? "s" : ""}`);
    return parts.length ? parts.join(", ") : "No approvers";
  };

  return (
    <Grid gutter="lg">
      <Grid.Col span={12} md={3}>
        <Paper
          withBorder
          p="md"
          radius="md"
          sx={(theme) => ({
            position: "sticky",
            top: theme.spacing.md,
          })}
        >
          <Text size="xs" weight={700} color="dimmed" tt="uppercase" mb="sm">
            Step navigation
          </Text>

          {steps.length === 0 ? (
            <Text size="sm" color="dimmed" mb="md">
              No steps yet. Add your first approval step below.
            </Text>
          ) : (
            <Timeline
              active={activeStep}
              bulletSize={28}
              lineWidth={2}
              color="teal"
            >
              {steps.map((step, idx) => {
                const isActive = activeStep === idx;
                const label = step.name?.trim() || `Step ${idx + 1}`;
                return (
                  <Timeline.Item
                    key={idx}
                    bullet={
                      <ThemeIcon
                        size={28}
                        radius="xl"
                        variant={isActive ? "filled" : "light"}
                        color={isActive ? "teal" : "gray"}
                      >
                        <Text size="xs" weight={700}>
                          {idx + 1}
                        </Text>
                      </ThemeIcon>
                    }
                    title={
                      <UnstyledButton
                        onClick={() => focusStep(idx)}
                        sx={{ display: "block", textAlign: "left", width: "100%" }}
                      >
                        <Text
                          size="sm"
                          weight={isActive ? 600 : 500}
                          lineClamp={2}
                          color={isActive ? undefined : "dimmed"}
                        >
                          {label}
                        </Text>
                        <Text size="xs" color="dimmed" mt={2} lineClamp={1}>
                          {approvalTypeLabel(step.approvalType)}
                        </Text>
                      </UnstyledButton>
                    }
                  />
                );
              })}
            </Timeline>
          )}

          <Button
            mt="md"
            fullWidth
            variant="light"
            leftIcon={<IconPlus size={16} />}
            onClick={addStep}
          >
            Add step
          </Button>
        </Paper>
      </Grid.Col>

      <Grid.Col span={12} md={9}>
        {steps.length === 0 ? (
          <Paper withBorder p="xl" radius="md">
            <Stack align="center" spacing="sm">
              <Text color="dimmed" size="sm" align="center">
                Approval steps define who reviews each stage of the workflow.
              </Text>
              <Button
                leftIcon={<IconPlus size={16} />}
                onClick={addStep}
              >
                Add first approval step
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Accordion
            multiple
            variant="separated"
            value={openAccordions}
            onChange={setOpenAccordions}
          >
            {steps.map((step, idx) => (
              <Accordion.Item key={idx} value={stepKey(idx)}>
                <Accordion.Control onClick={() => setActiveStep(idx)}>
                  <Group position="apart" noWrap sx={{ flex: 1, pr: "xs" }}>
                    <Group spacing="sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
                      <ThemeIcon
                        color="teal"
                        variant="light"
                        size={32}
                        radius="xl"
                      >
                        <Text size="sm" weight={700}>
                          {idx + 1}
                        </Text>
                      </ThemeIcon>
                      <Box sx={{ minWidth: 0 }}>
                        <Group spacing="xs" noWrap>
                          <Text size="sm" weight={600} lineClamp={1}>
                            {step.name?.trim() || `Step ${idx + 1}`}
                          </Text>
                          <Badge size="xs" variant="outline" color="gray">
                            {approvalTypeLabel(step.approvalType)}
                          </Badge>
                        </Group>
                        <Text size="xs" color="dimmed" lineClamp={1}>
                          {approverSummary(step)}
                          {(step.checklist?.length ?? 0) > 0 &&
                            ` · ${step.checklist.length} checklist item${
                              step.checklist.length > 1 ? "s" : ""
                            }`}
                          {(step.setValues?.length ?? 0) > 0 &&
                            ` · ${step.setValues.length} set value${
                              step.setValues.length > 1 ? "s" : ""
                            }`}
                          {(step.stepFields ?? []).some((f) => isFileFieldType(f.inputType)) &&
                            ` · ${
                              (step.stepFields ?? []).filter((f) =>
                                isFileFieldType(f.inputType)
                              ).length
                            } file field${
                              (step.stepFields ?? []).filter((f) =>
                                isFileFieldType(f.inputType)
                              ).length > 1
                                ? "s"
                                : ""
                            }`}
                        </Text>
                      </Box>
                    </Group>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          disabled={steps.length === 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeStep(idx);
                          }}
                        >
                          Remove
                        </Menu.Item>
                        <Menu.Item
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateStep(idx);
                          }}
                        >
                          Duplicate
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Box
                    ref={(el) => {
                      panelRefs.current[stepKey(idx)] = el;
                    }}
                  >
                    <StepFields
                      step={step}
                      idx={idx}
                      updateStep={updateStep}
                      updateChecklist={updateChecklist}
                      updateSetValues={updateSetValues}
                      updateNotifications={updateNotifications}
                      updateStepFields={updateStepFieldsConfig}
                      statusFields={statusFields}
                    />
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </Grid.Col>
    </Grid>
  );
};

export default McaStepBuilder;
